import type {SharedData} from '@gaia/types';
import fetchJSON from '@gaia/utils/fetchJSON';
import fetchElevation, {hasMissingElevation} from '@gaia/utils/gaia/fetchElevation';
import getLineLocalId from '@gaia/utils/gaia/getLineLocalId';
import {registerSavedTrack} from '@gaia/utils/gaia/saveTrack';
import {registerSavedWaypoint} from '@gaia/utils/gaia/saveWaypoint';
import length from '@turf/length';
import lineSlice from '@turf/line-slice';
import turfNearestPoint from '@turf/nearest-point-on-line';
import type {LineString} from 'geojson';
import every from 'lodash/every';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import uniqueId from 'lodash/uniqueId';

export default function takeLocal(
    action: TakeLocalTrackAction,
    tracks: {[localId: string]: Track},
    setTracks: React.Dispatch<React.SetStateAction<{[localId: string]: Track}>>,
    sharedData: SharedData
) {
    const {track} = action;
    const {id, localId} = track.properties;

    if (localId && tracks[localId]) {
        if (action.callback) action.callback(tracks[localId]);
        return [localId];
    }

    // A track that is already local
    if (id && tracks[id]) {
        if (action.callback) action.callback(tracks[id]);
        return [id];
    }

    if (!id) throw new Error('Cannot call takeLocal on a track without an id');

    const initialTrack: Track = {
        ...action.track,
        isGeometryLoaded: false,
        properties: {
            id,
            localId: id,
            archived: false,
            routing_mode: 'pedestrian',
            title: '',
            notes: '',
            isLocallyCreated: false,
            type: 'track',
            isPublicTrack: false,

            // We encode "null" as a string in vector tiles
            ...(mapValues(track.properties, (value) =>
                value === 'null' ? null : value
            ) as Partial<Track['properties']>),
        },
    };

    registerSavedTrack(initialTrack);

    if (!tracks[id]) {
        setTracks((latestTracks) => ({
            ...latestTracks,
            [id]: initialTrack,
        }));
    }

    const pubLink: string =
        sharedData &&
        (sharedData.type === 'track' || sharedData.type === 'folder') &&
        sharedData.pubLink
            ? `?pubLink=${sharedData.pubLink}`
            : '';

    fetchJSON(`/api/objects/track/${id}/${pubLink}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json'},
    })
        .then((collection) => {
            const unmodifiedMultiline: RemoteTrack = find(
                collection.features,
                (feature: any) => feature.geometry.type === 'MultiLineString'
            );
            const remoteTrackProperties = collection.features[0].properties;

            const unmodifiedLineGeometry: LineString = {
                type: 'LineString',
                coordinates: flatten(unmodifiedMultiline.geometry.coordinates),
            };

            fetchElevation(unmodifiedLineGeometry)
                .then((elevatedUnmodifiedLine) => {
                    // If the track was missing elevation setting this property to true forces a save of the
                    // track, ensuring that the elevation does not need to be looked up on subsequent loads.
                    // hasMissingElevation is checked again to ensure that fetchElevation did not return NO_DATA
                    const isElevationModified =
                        !isEqual(unmodifiedLineGeometry, elevatedUnmodifiedLine) &&
                        !hasMissingElevation(elevatedUnmodifiedLine);

                    const unmodifiedLine: TrackLine = {
                        type: 'Feature',
                        properties: {
                            id,
                            localId: id,
                            fromPointLocalId: undefined,
                            toPointLocalId: undefined,
                            type: 'normal',
                            lineLocalId: uniqueId('line'),
                            isGeometryLoaded: true,
                        },
                        geometry: elevatedUnmodifiedLine,
                    };

                    const unmodifiedPoints = track.properties.isPublicTrack
                        ? []
                        : collection.features.filter(
                              (feature: RemoteTrack | RemoteTrackPoint) =>
                                  feature.geometry.type === 'Point'
                          );

                    const hasOrderProperty = every(
                        unmodifiedPoints,
                        (point: RemoteTrackPoint) =>
                            point.properties.order !== null && point.properties.order !== undefined
                    );

                    const points: TrackPoint[] = sortBy(
                        unmodifiedPoints,
                        (point: RemoteTrackPoint) => {
                            if (hasOrderProperty) {
                                return point.properties.order;
                            } else {
                                return getLinePosition(unmodifiedLine, point);
                            }
                        }
                    ).map((remotePoint: RemoteTrackPoint, order: number) => {
                        const point: TrackPoint = {
                            ...remotePoint,
                            properties: {
                                ...remotePoint.properties,
                                trackLocalId: id,
                                track_id: id,
                                localId: remotePoint.properties.id || uniqueId('point'),
                                archived: false,
                                isGeometryModified: false,
                                isLocallyCreated: false,
                                order,
                                type: 'waypoint',
                            },
                        };
                        registerSavedWaypoint(point);
                        return point;
                    });

                    if (remoteTrackProperties.track_type !== 'route') {
                        setTracks((latestTracks) => {
                            const latestTrack: Track = {
                                properties: {
                                    id,
                                    ...initialTrack.properties,
                                    ...remoteTrackProperties,
                                    isElevationModified,
                                },
                                points: keyBy(points, 'properties.localId'),
                                unmodifiedLine,
                                isGeometryLoaded: true,
                            };
                            // Skipping registerSavedTrack when the elevation has been modified forces a save,
                            // allowing us to skip elevation lookups on subsequent requests
                            if (!isElevationModified) registerSavedTrack(latestTrack);
                            if (action.callback) action.callback(latestTrack);
                            return {
                                ...latestTracks,
                                [id]: latestTrack,
                            };
                        });
                    } else {
                        const unmodifiedLines: TrackLine[] = generateRouteSegments(
                            unmodifiedLine,
                            points,
                            id
                        );

                        const latestTrack: Track = {
                            properties: {
                                id,
                                ...initialTrack.properties,
                                ...remoteTrackProperties,
                                routing_mode: remoteTrackProperties.routing_mode || 'pedestrian',
                                isElevationModified,
                            },
                            isGeometryLoaded: true,
                            points: keyBy(points, 'properties.localId'),
                            unmodifiedLines: keyBy(unmodifiedLines, 'properties.lineLocalId'),
                            unmodifiedLine,
                        };

                        // Skipping registerSavedTrack when the elevation has been modified forces a save,
                        // allowing us to skip elevation lookups on subsequent requests
                        if (!isElevationModified) registerSavedTrack(latestTrack);
                        if (action.callback) action.callback(latestTrack);

                        setTracks((latestTracks) => ({
                            ...latestTracks,
                            [id]: latestTrack,
                        }));
                    }
                })
                .catch(console.warn);
        })
        .catch(console.warn);

    return [id];
}

/**
 * Given a linestring and an ordered collection of points, slices the line string into point-to-point segments.
 * Note: Points are assumed to be sorted by their `order` property, so this will not behave as expected for routes
 * where points are out of order compared to their geometry's direction of travel.
 * @param unmodifiedLine The unmodified route geometry
 * @param points A sorted array of route points
 * @param id The route ID
 * @returns an array of linestring features between each pair of ordered route points
 */
export function generateRouteSegments(unmodifiedLine: TrackLine, points: TrackPoint[], id: string) {
    let workingLine = unmodifiedLine.geometry.coordinates;
    const unmodifiedLines: TrackLine[] = range(points.length - 1).map((index) => {
        const fromPoint = points[index];
        const toPoint = points[index + 1];
        const lineLocalId = getLineLocalId(id, fromPoint, toPoint);

        if (!fromPoint && !toPoint) {
            // Imported routes have no route points, so we just return the original geometry
            return {
                type: 'Feature',
                properties: {
                    id,
                    localId: id,
                    track_id: id,
                    fromPointLocalId: undefined,
                    toPointLocalId: undefined,
                    type: 'normal',
                    lineLocalId,
                    isGeometryLoaded: true,
                },
                geometry: unmodifiedLine.geometry,
            };
        }

        const workingLineString: LineString = {
            type: 'LineString',
            coordinates: workingLine,
        };
        const slice = lineSlice(fromPoint, toPoint, workingLineString);

        const slicePoint = turfNearestPoint(workingLineString, toPoint.geometry.coordinates);
        workingLine = workingLine.slice(slicePoint.properties.index);

        return {
            type: 'Feature',
            properties: {
                id,
                localId: id,
                track_id: id,
                fromPointLocalId: fromPoint.properties.localId,
                toPointLocalId: toPoint.properties.localId,
                type: 'normal',
                lineLocalId,
                isGeometryLoaded: true,
            },
            geometry: slice.geometry!,
        };
    });
    return unmodifiedLines;
}

// We do not store the index of each track point. We guess at the sort order using the geometry.
// https://github.com/trailbehind/GaiaCloud/blob/master/gaia/static/js/app/util/TBGeoUtil.js#L4-L13
function getLinePosition(line: TrackLine, point: RemoteTrackPoint) {
    const totalDistance = length(line);
    const lineBeforePoint = lineSlice(
        {
            coordinates: line.geometry.coordinates[0],
            type: 'Point',
        },
        point,
        line
    );
    const distanceBeforePoint = length(lineBeforePoint);
    return distanceBeforePoint / totalDistance;
}
